import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface DemoCardData {
  name: string;
  industry: string;
  blurb: string;
  href: string;
  status?: 'live' | 'coming-soon';
}

/** A card in the demo gallery linking to a live Acme mock storefront. */
export function DemoCard({ demo, className }: { demo: DemoCardData; className?: string }) {
  const live = demo.status !== 'coming-soon';
  const Wrapper = (live ? 'a' : 'div') as 'a';
  return (
    <Wrapper
      {...(live ? { href: demo.href } : {})}
      className={cn(
        'flex flex-col rounded-2xl border border-brand-sage/15 bg-card p-6 shadow-brand transition-colors',
        live ? 'hover:border-primary/40' : 'opacity-70',
        className,
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-primary">
          {demo.industry}
        </span>
        {live ? null : (
          <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">Coming soon</span>
        )}
      </div>
      <h3 className="text-lg font-semibold">{demo.name}</h3>
      <p className="mt-1 flex-1 text-sm text-muted-foreground">{demo.blurb}</p>
      {live ? <span className="mt-4 text-sm font-medium text-primary">Try the live demo →</span> : null}
    </Wrapper>
  );
}

export interface IndustryHeroProps {
  industry: string;
  headline: ReactNode;
  subtitle?: ReactNode;
  primaryCta?: { label: string; href: string };
  demoCta?: { label: string; href: string };
  className?: string;
}

export function IndustryHero({
  industry,
  headline,
  subtitle,
  primaryCta,
  demoCta,
  className,
}: IndustryHeroProps) {
  return (
    <section className={cn('mx-auto max-w-4xl px-6 py-20 text-center', className)}>
      <p className="mb-3 text-sm font-semibold tracking-wide text-primary">{industry}</p>
      <h1 className="text-balance text-4xl font-semibold tracking-tight sm:text-5xl">{headline}</h1>
      {subtitle ? (
        <p className="mx-auto mt-5 max-w-2xl text-balance text-lg text-muted-foreground">{subtitle}</p>
      ) : null}
      {primaryCta || demoCta ? (
        <div className="mt-8 flex items-center justify-center gap-3">
          {primaryCta ? (
            <a
              href={primaryCta.href}
              className="inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {primaryCta.label}
            </a>
          ) : null}
          {demoCta ? (
            <a
              href={demoCta.href}
              className="inline-flex h-11 items-center rounded-md border border-brand-sage/30 bg-card px-6 text-sm font-medium hover:bg-brand-cream-shadow"
            >
              {demoCta.label}
            </a>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
